import { useState, useEffect } from "react";
import { card, btnPrimary, btnSecondary, SectionTitle } from "./ui.js";
import { usagePeriodLabel } from "./usagePeriod.js";
import { api } from "../api.js";

interface PlanInfo {
  key: string;
  label: string;
  monthlyPrice: number;
  monthlyLimit: number | "unlimited";
}

// 内部キー（starter/standard/pro）は既存互換のため保持、表示ラベルのみ変更
const PLANS: PlanInfo[] = [
  { key: "starter", label: "Starter", monthlyPrice: 4980, monthlyLimit: 30 },
  { key: "standard", label: "Professional", monthlyPrice: 9800, monthlyLimit: 100 },
  { key: "pro", label: "Enterprise", monthlyPrice: 19800, monthlyLimit: "unlimited" },
];

const PLAN_LABELS: Record<string, string> = { free: "Free", starter: "Starter", standard: "Professional", pro: "Enterprise" };

export default function BillingTab({
  /**
   * Stripe からの戻り。`success` なら決済完了、`cancel` なら中断。
   * 以前は URL の status を読んでおらず、決済しても何も表示されなかった。
   */
  status,
}: {
  status?: string | null;
} = {}) {
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);

  const reloadTenant = () => api.getMe().then(setTenant).catch(() => {});

  useEffect(() => {
    reloadTenant().finally(() => setLoading(false));
    // Stripe Checkout から戻ってきた直後は Webhook 反映に数秒かかるため、少し待ってから再取得
    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "success") {
      const timer = setTimeout(() => reloadTenant(), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handlePortal = async () => {
    try {
      const { url } = await api.createPortal();
      window.open(url, "_blank");
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleCheckout = async (plan: string) => {
    setCheckingOut(plan);
    try {
      const { url } = await api.createCheckout(plan);
      window.location.href = url;
    } catch (e: any) {
      alert(e.message);
    } finally {
      setCheckingOut(null);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>読み込み中...</div>;

  const currentPlan = tenant?.plan || "free";
  // 表示する上限は送信判定と同じ値（無料トライアル中は monthlyLimit が 0 のままのため）
  const quota = tenant?.sendQuota;
  const limit = quota?.effectiveMonthlyLimit ?? tenant?.monthlyLimit ?? 0;
  const period = usagePeriodLabel(tenant);
  // signupTrial.active は契約なし（monthlyLimit=0）のときだけ true になる
  const inTrial = !!quota?.signupTrial?.active;
  const isInternal = !!quota?.isInternal;
  const trialEndsAt = quota?.signupTrial?.endsAt
    ? new Date(quota.signupTrial.endsAt).toLocaleDateString("ja-JP")
    : null;
  const used = tenant?.currentMonthUsage || 0;
  const hasSub = !!tenant?.hasStripeSubscription;
  // 過去に契約履歴あり（解約後でも明細は参照可能）
  const hasStripeHistory = !!tenant?.hasStripeCustomer;
  const currentPlanInfo = PLANS.find((p) => p.key === currentPlan);

  return (
    <>
      <SectionTitle>プラン・請求</SectionTitle>

      {status === "success" && (
        <div style={{ padding: "14px 16px", borderRadius: 10, background: "#E1F5EE", color: "#0F6E56", fontSize: 15, marginBottom: 16, lineHeight: 1.7 }}>
          <b>お手続きが完了しました。</b>
          <br />
          プランの反映には少し時間がかかることがあります。表示が変わらない場合はページを再読み込みしてください。
        </div>
      )}
      {status === "cancel" && (
        <div style={{ padding: "14px 16px", borderRadius: 10, background: "#FAEEDA", color: "#854F0B", fontSize: 15, marginBottom: 16, lineHeight: 1.7 }}>
          お手続きを中断しました。プランは変更されていません。
        </div>
      )}

      {/* 現在のプラン（大きく目立つカード） */}
      <div style={{
        ...card,
        background: hasSub ? "linear-gradient(135deg, #534AB7 0%, #7F77DD 100%)" : "white",
        color: hasSub ? "white" : "#1a1a1a",
        border: hasSub ? "none" : "1px solid #e8e7e3",
      }}>
        <div style={{ fontSize: 13, opacity: hasSub ? 0.8 : 1, color: hasSub ? "white" : "#6b6b68", marginBottom: 8, fontWeight: 500 }}>
          {hasSub ? "契約中のプラン" : "現在のプラン"}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 32, fontWeight: 700, lineHeight: 1.1, marginBottom: 6 }}>
              {!hasSub && inTrial ? "無料トライアル中" : PLAN_LABELS[currentPlan] || currentPlan}
              {hasSub && (
                <span style={{
                  marginLeft: 12,
                  padding: "4px 10px",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  background: "rgba(255,255,255,0.2)",
                  color: "white",
                  verticalAlign: "middle",
                }}>
                  契約中
                </span>
              )}
            </div>
            {hasSub && currentPlanInfo ? (
              <div style={{ fontSize: 14, opacity: 0.9 }}>
                ¥{currentPlanInfo.monthlyPrice.toLocaleString()} / 月（税別）・ 月間 {currentPlanInfo.monthlyLimit === "unlimited" ? "無制限" : `${currentPlanInfo.monthlyLimit}件`}
              </div>
            ) : (
              <div style={{ fontSize: 14, color: "#6b6b68", lineHeight: 1.7 }}>
                {isInternal
                  ? "社内アカウントのため、プランのお申し込みなしで送信できます。"
                  : inTrial
                    ? `${trialEndsAt} まで、Professional プラン相当（月間 ${limit} 件）で送信できます。期間終了後に送信を続けるには、プランのお申し込みが必要です。`
                    : "無料トライアル期間は終了しました。書類を送信するには、プランのお申し込みが必要です。"}
              </div>
            )}
          </div>
          {hasSub && (
            <button onClick={handlePortal} style={{
              background: "white",
              color: "#534AB7",
              border: "none",
              borderRadius: 8,
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}>
              プラン・支払い方法を変更
            </button>
          )}
        </div>
      </div>

      {/*
        利用状況。送信件数は請求期間（契約日基準）ごとに数え直す（services/webhook-handler.ts）。
        期間が分かるときは「今月」ではなく期間と次のリセット日を出す。契約が無いときは従来どおり
      */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {period ? `今回の請求期間の利用状況（${period.range}）` : "今月の利用状況"}
          </div>
          {period && <div style={{ fontSize: 13, color: "#6b6b68" }}>送信件数は {period.nextReset} に 0 に戻ります</div>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
          <div style={{ background: "#f5f4f0", borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 14, color: "#6b6b68", marginBottom: 6 }}>送信件数</div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>{used} {limit > 0 && limit < 999999 && <span style={{ fontSize: 14, color: "#6b6b68" }}>/ {limit}</span>}</div>
          </div>
          <div style={{ background: "#f5f4f0", borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 14, color: "#6b6b68", marginBottom: 6 }}>{period ? "今回の請求期間の料金（税別）" : "今月の請求見込み（税別）"}</div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>
              ¥{(currentPlanInfo?.monthlyPrice || 0).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* プラン一覧（契約中は切替、未契約は選択） */}
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
          {hasSub ? "プランを変更する" : "プランを選択"}
        </div>
        <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 6 }}>
          {hasSub
            ? "契約中以外のプランに切り替える場合は、Stripe カスタマーポータルから変更してください。"
            : "最適なプランを選んでクラウドサイン連携を開始"}
        </div>
        <div style={{ fontSize: 12, color: "#999", marginBottom: 20 }}>
          表示価格はすべて税別です。決済時に消費税 10% が加算されます。
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 16 }}>
          {PLANS.map((p) => {
            const price = p.monthlyPrice;
            const isCurrent = hasSub && p.key === currentPlan;
            const isRecommended = !hasSub && p.key === "standard";
            const highlight = isCurrent || isRecommended;

            return (
              <div key={p.key} style={{
                border: isCurrent
                  ? "2px solid #1D9E75"
                  : isRecommended ? "2px solid #534AB7" : "1px solid #d0cfc9",
                borderRadius: 12,
                padding: "24px 20px",
                background: isCurrent ? "#E1F5EE" : isRecommended ? "#EEEDFE" : "white",
                position: "relative",
              }}>
                {isCurrent && (
                  <span style={{ position: "absolute", top: -10, left: 16, padding: "3px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, background: "#1D9E75", color: "white" }}>
                    契約中
                  </span>
                )}
                {isRecommended && !isCurrent && (
                  <span style={{ position: "absolute", top: -10, left: 16, padding: "2px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, background: "#534AB7", color: "white" }}>
                    おすすめ
                  </span>
                )}
                <div style={{ fontSize: 14, fontWeight: 600, color: highlight ? (isCurrent ? "#0F6E56" : "#534AB7") : "#1a1a1a", marginBottom: 4 }}>
                  {p.label}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 10 }}>
                  ¥{price.toLocaleString()}
                  <span style={{ fontSize: 13, fontWeight: 400, color: "#6b6b68" }}> / 月（税別）</span>
                </div>
                <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 16, lineHeight: 1.8 }}>
                  <div>月間 {p.monthlyLimit === "unlimited" ? "無制限" : `${p.monthlyLimit}件`}</div>
                  <div>クレジットカード決済</div>
                </div>

                {isCurrent ? (
                  <button disabled style={{
                    width: "100%",
                    padding: "10px 24px",
                    fontSize: 14,
                    fontWeight: 600,
                    border: "none",
                    borderRadius: 8,
                    background: "#1D9E75",
                    color: "white",
                    cursor: "default",
                  }}>
                    契約中
                  </button>
                ) : hasSub ? (
                  <button onClick={handlePortal} style={{ ...btnSecondary, width: "100%" }}>
                    このプランに変更
                  </button>
                ) : (
                  <button onClick={() => handleCheckout(p.key)} disabled={!!checkingOut}
                    style={{ ...(isRecommended ? btnPrimary : btnSecondary), width: "100%", opacity: checkingOut ? 0.6 : 1 }}>
                    {checkingOut === p.key ? "処理中..." : `${p.label}で開始`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* 申し込み時の課金開始の説明（未契約時のみ） */}
        {!hasSub && (
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "#EEEDFE", fontSize: 13, color: "#534AB7", lineHeight: 1.7, border: "1px solid #d3cff0" }}>
            <strong>お申し込みについて</strong>：お申し込みいただいた日から課金が始まります。
            {inTrial && "無料トライアル期間中にお申し込みいただいた場合も、その日から課金が始まります（残りのトライアル期間は引き継がれません）。"}
            無料トライアルの終了によって自動で課金されることはありません。
          </div>
        )}
      </div>

      {/* Stripe カスタマーポータル（契約中 or 過去契約あり）*/}
      {hasStripeHistory && (
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>請求・明細の管理</div>
          <div style={{ fontSize: 14, color: "#6b6b68", marginBottom: 18 }}>
            {hasSub
              ? "支払い方法の更新、請求書のダウンロード、サブスクリプションのキャンセルは Stripe の管理画面で行えます。"
              : "過去の請求書や領収書は Stripe の管理画面から確認・ダウンロードできます。"}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button style={btnPrimary} onClick={handlePortal}>
              請求履歴・領収書を確認
            </button>
            {hasSub && (
              <>
                <button style={btnSecondary} onClick={handlePortal}>支払い方法を変更</button>
                <button style={btnSecondary} onClick={handlePortal}>サブスクリプションを解約</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* 支払い方法の説明 */}
      <div style={{ ...card, borderLeft: "3px solid #378ADD", borderRadius: "0 12px 12px 0" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#185FA5", marginBottom: 8 }}>支払い方法について</div>
        <div style={{ fontSize: 14, color: "#6b6b68", lineHeight: 1.8 }}>
          全プランで <strong>クレジットカード決済</strong> をご利用いただけます。
          <br />
          <strong>請求書払い（銀行振込）</strong>をご希望の場合は、
          <a href="/contact" style={{ color: "#185FA5", fontWeight: 500 }}>お問い合わせフォーム</a>
          よりお問い合わせください。
        </div>
      </div>

    </>
  );
}
